import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { joinPresent } from '../mentorDashboardFormat';
import MenteePortalNav from './MenteePortalNav';
import MenteeMyMentorHeader from './MenteeMyMentorHeader';
import MenteeMyMentorBasicDetails from './MenteeMyMentorBasicDetails';
import MenteeMyMentorAbout from './MenteeMyMentorAbout';
import MenteeMyMentorTracks from './MenteeMyMentorTracks';
import MenteeMyMentorAvailability from './MenteeMyMentorAvailability';
import MenteeDashboardThreadModal from './MenteeDashboardThreadModal';
import MenteePortalService, {
    MenteeLanding,
    MenteeMentorProfilePayload,
} from './MenteePortalService';
import '../mentorDashboard.css';
import './menteeRegistration.css';
import './menteeDashboard.css';
import './menteeMyMentor.css';

const INELIGIBLE_COPY = 'The mentorship program is not available for your campus';

/**
 * UAS-2380 My Mentor page (DEC-P17-*).
 * Send Message reuses P16 MenteeDashboardThreadModal — no duplicate thread UI.
 */
const MenteeMyMentor = () => {
    const [loading, setLoading] = useState(true);
    const [landing, setLanding] = useState<MenteeLanding | null>(null);
    const [payload, setPayload] = useState<MenteeMentorProfilePayload | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [ineligible, setIneligible] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [unpaired, setUnpaired] = useState(false);
    const [threadOpen, setThreadOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            MenteePortalService.getDashboard(),
            MenteePortalService.getMentorProfile().catch((err: { status?: number }) => {
                if (err?.status === 404) {
                    return null;
                }
                throw err;
            }),
        ])
            .then(([dash, profile]) => {
                setLanding(dash.landing);
                setIneligible(false);
                setForbidden(false);
                setLoadError(null);
                if (dash.landing !== 'dashboard') {
                    setPayload(null);
                    setUnpaired(false);
                    return;
                }
                if (!profile) {
                    setUnpaired(true);
                    setPayload(null);
                    return;
                }
                setUnpaired(false);
                setPayload(profile);
            })
            .catch((err: { status?: number; message?: string }) => {
                const status = err?.status;
                const message = MenteePortalService.errorMessage(err);
                if (status === 403 && /ineligible/i.test(message)) {
                    setIneligible(true);
                    setLoadError(null);
                    return;
                }
                if (status === 403 || status === 401) {
                    setForbidden(true);
                    setLoadError(null);
                    return;
                }
                setLoadError(message);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading && !payload && landing === null && !unpaired) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">Loading mentor profile…</p>
            </div>
        );
    }

    if (ineligible) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">{INELIGIBLE_COPY}</p>
            </div>
        );
    }

    if (forbidden) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">You do not have access to Mentorship.</p>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={landing} />
                <p className="mreg-banner is-danger">{loadError}</p>
            </div>
        );
    }

    if (landing && landing !== 'dashboard') {
        return <Navigate to="/alumni/mentorship/mentee/register" replace />;
    }

    if (unpaired || !payload) {
        return (
            <div className="mreg mmentor max-1250 mx-auto px-3 py-4">
                <MenteePortalNav landing={landing} />
                
                <div className="card border-0 rounded-3 px-3 py-2 bg-white d-flex flex-row align-items-center justify-content-between shadow-sm mb-3">
                    <div className="breadcrumb mb-0 align-items-center font-12 fw-medium text-secondary">
                        Mentorship Module &nbsp;›&nbsp; <span className="text-dark fw-bold" id="bc-cur">My Mentor</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 text-secondary font-12">
                        <button className="btn btn-sm btn-light border-0" title="Help"><i className="bi bi-question-circle"></i></button>
                        <button className="btn btn-sm btn-light border-0 position-relative" title="Notifications">
                        <i className="bi bi-bell"></i>
                        <span className="position-absolute top-2 start-75 translate-middle p-1 bg-danger border border-white rounded-circle"></span>
                        </button>
                    </div>
                    </div>

                <section className="mmentor-card">
                    <h1 className="mmentor-card-title">My Mentor</h1>
                    <p className="mmentor-empty">
                        A mentor has not been published for you yet. Check back when matching is
                        published.
                    </p>
                </section>
            </div>
        );
    }

    return (
        <div className="mreg mmentor max-1250 mx-auto px-3 py-4">
            <MenteePortalNav landing={landing} />
            <div className="card border-0 rounded-3 px-3 py-2 bg-white d-flex flex-row align-items-center justify-content-between shadow-sm mb-3">
                    <div className="breadcrumb mb-0 align-items-center font-12 fw-medium text-secondary">
                        Mentorship Module &nbsp;›&nbsp; <span className="text-dark fw-bold" id="bc-cur">My Mentor</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 text-secondary font-12">
                        <button className="btn btn-sm btn-light border-0" title="Help"><i className="bi bi-question-circle"></i></button>
                        <button className="btn btn-sm btn-light border-0 position-relative" title="Notifications">
                        <i className="bi bi-bell"></i>
                        <span className="position-absolute top-2 start-75 translate-middle p-1 bg-danger border border-white rounded-circle"></span>
                        </button>
                    </div>
                    </div>

            <MenteeMyMentorHeader payload={payload} onSendMessage={() => setThreadOpen(true)} />
            <MenteeMyMentorBasicDetails
                identity={payload.identity}
                tracks={payload.careerTracks || []}
                onSendMessage={() => setThreadOpen(true)}
            />

            <div className="row g-3 mb-3">
                <MenteeMyMentorAbout mentorName={payload.identity.name} bio={payload.bio} />

                <MenteeMyMentorTracks
                    tracks={payload.careerTracks || []}
                    specificAreas={payload.specificAreas}
                />
            </div>
            

            <MenteeMyMentorAvailability
                    mentorName={payload.identity.name}
                    slotDurationMinutes={payload.availability?.slotDurationMinutes ?? 45}
                    slots={payload.availability?.slots || []}
                />

            <MenteeDashboardThreadModal
                open={threadOpen}
                onClose={() => setThreadOpen(false)}
                onSent={() => {
                    /* thread self-updates; dashboard checklist not on this page */
                }}
                mentorName={payload.identity.name}
                mentorRole={joinPresent([payload.identity.designation, payload.identity.company], ' · ')}
            />
        </div>
    );
};

export default MenteeMyMentor;
